/* eslint-disable @typescript-eslint/consistent-type-definitions -- we need to use `interface` in order to override MUIs interfaces*/
import { type ThemeOptions } from '@mui/material/styles';

export const elevateColorPalette = {
  text: {
    primary: 'rgba(0, 0, 0, 0.91)',
    primaryOnAccent: 'rgba(0, 0, 0, 0.91)',
    secondary: 'rgba(0, 0, 0, 0.73)',
    secondaryOnAccent: 'rgba(0, 0, 0, 0.73)',
    tertiary: 'rgba(0, 0, 0, 0.54)',
    primaryOnBrand: '#FFFFFF',
    secondaryOnBrand: 'rgba(255, 255, 255, 0.86)',
    tertiaryOnBrand: 'rgba(255, 255, 255, 0.63)',
    error: '#E11920',
    disabled: 'rgba(0, 0, 0, 0.23)',
    disabledSolid: '#757575',
    brandLight: '#F7F0FF',
    brandLightHover: '#E8D6FF',
    brand: '#6F01F5',
    brandHover: '#4C06A1',
    brandDark: '#190333',
    brandDarkOnAccent: '#190333',
    accent: '#EB47CD',
    accentDark: '#9C1183',
  },
  surface: {
    accent: '#EB47CD',
    accentLight: '#FEF1FC',
    background: '#FFFFFF',

    recessed: '#FFFFFF',
    elevated: '#F5F5F5',
    elevatedHover: '#EBEBEB',
    overlay: 'rgba(0, 0, 0, 0.23)',
    disabled: 'rgba(0, 0, 0, 0.05)',
    disabledSolid: '#DBDBDB',
    brandLight: '#F7F0FF',
    brandLightHover: '#E8D6FF',
    brand: '#6F01F5',
    brandHover: '#4C06A1',
    brandDark: '#190333',
    brandDarkHover: '#4C06A1',
    brandDarkOnAccentLight: '#190333',
    brandDarkHoverOnAccentLight: '#4C06A1',
  },
  border: {
    subtle: 'rgba(0,0,0,0.08)',
    default: 'rgba(0,0,0,0.14)',
    emphasised: 'rgba(0,0,0,0.54)',
    strong: 'rgba(0,0,0,0.91)',
    active: '#6F01F5',
    focus: '#6F01F5',
    error: '#E11920',
    disabled: 'rgba(0,0,0,0.08)',
  },
  feedback: {
    green: '#14A51A',
    amber: '#FCB603',
  },
};

export const calendarColors = {
  notBookable: '#ffffff',
  bookableResource: '#14A51A1A',
  previousSelection: '#4C06A1',
  selectionArea: '#6F01F533',
  selectedBlockung: '#6F01F5',
  externalBlockung: '#035407',
  internalBlockung: '#035407',
  weekend: '#00000016',
  accordion: '#190333',
  accordionText: '#FFFFFF',
  border: '#DBDBDB',
  resource: '#FFFFFF',
  errorIcon: '#E11920',
  warningIcon: '#FCB603',
};

export const colorPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: elevateColorPalette.surface.brand,
    dark: elevateColorPalette.surface.brandHover,
    light: elevateColorPalette.surface.brand,
    contrastText: elevateColorPalette.text.primaryOnBrand,
  },
  secondary: {
    main: elevateColorPalette.surface.brandLight,
    dark: elevateColorPalette.surface.brandLightHover,
    light: elevateColorPalette.surface.brandLight,
    contrastText: elevateColorPalette.text.brand,
  },
  text: {
    primary: elevateColorPalette.text.primary,
    primaryOnAccent: elevateColorPalette.text.primaryOnAccent,
    secondary: elevateColorPalette.text.secondary,
    secondaryOnAccent: elevateColorPalette.text.secondaryOnAccent,
    tertiary: elevateColorPalette.text.tertiary,
    primaryOnBrand: elevateColorPalette.text.primaryOnBrand,
    secondaryOnBrand: elevateColorPalette.text.secondaryOnBrand,
    tertiaryOnBrand: elevateColorPalette.text.tertiaryOnBrand,
    error: elevateColorPalette.text.error,
    disabled: elevateColorPalette.text.disabled,
    disabledSolid: elevateColorPalette.text.disabledSolid,
    brandLight: elevateColorPalette.text.brandLight,
    brandLightHover: elevateColorPalette.text.brandLightHover,
    brand: elevateColorPalette.text.brand,
    brandHover: elevateColorPalette.text.brandHover,
    brandDark: elevateColorPalette.text.brandDark,
    brandDarkOnAccent: elevateColorPalette.text.brandDarkOnAccent,
    accent: elevateColorPalette.text.accent,
    accentDark: elevateColorPalette.text.accentDark,
  },
  surface: {
    accent: elevateColorPalette.surface.accent,
    accentLight: elevateColorPalette.surface.accentLight,
    background: elevateColorPalette.surface.background,
    recessed: elevateColorPalette.surface.recessed,
    elevated: elevateColorPalette.surface.elevated,
    elevatedHover: elevateColorPalette.surface.elevatedHover,
    overlay: elevateColorPalette.surface.overlay,
    disabled: elevateColorPalette.surface.disabled,
    disabledSolid: elevateColorPalette.surface.disabledSolid,
    brandLight: elevateColorPalette.surface.brandLight,
    brandLightHover: elevateColorPalette.surface.brandLightHover,
    brand: elevateColorPalette.surface.brand,
    brandHover: elevateColorPalette.surface.brandHover,
    brandDark: elevateColorPalette.surface.brandDark,
    brandDarkHover: elevateColorPalette.surface.brandDarkHover,
    brandDarkOnAccentLight: elevateColorPalette.surface.brandDarkOnAccentLight,
    brandDarkHoverOnAccentLight: elevateColorPalette.surface.brandDarkHoverOnAccentLight,
  },
  border: {
    subtle: elevateColorPalette.border.subtle,
    default: elevateColorPalette.border.default,
    emphasised: elevateColorPalette.border.emphasised,
    strong: elevateColorPalette.border.strong,
    active: elevateColorPalette.border.active,
    focus: elevateColorPalette.border.focus,
    error: elevateColorPalette.border.error,
    disabled: elevateColorPalette.border.disabled,
  },
  background: {
    default: elevateColorPalette.surface.background,
    subtle: elevateColorPalette.surface.elevated,
  },
  divider: elevateColorPalette.border.default,
  success: {
    main: elevateColorPalette.feedback.green,
  },
  warning: {
    main: elevateColorPalette.feedback.amber,
  },
  error: {
    main: elevateColorPalette.text.error,
  },
  DataGrid: {
    headerBg: 'inherit',
  },
};

declare module '@mui/material/styles' {
  interface Palette {
    surface: TypeSurface;
    border: TypeBorder;
  }

  interface PaletteOptions {
    surface?: TypeSurface;
    border?: TypeBorder;
    DataGrid?: {
      headerBg?: string;
    };
  }

  interface TypeText {
    primary: string;
    primaryOnAccent: string;
    secondary: string;
    secondaryOnAccent: string;
    tertiary: string;
    primaryOnBrand: string;
    secondaryOnBrand: string;
    tertiaryOnBrand: string;
    error: string;
    disabled: string;
    disabledSolid: string;
    brandLight: string;
    brandLightHover: string;
    brand: string;
    brandHover: string;
    brandDark: string;
    brandDarkOnAccent: string;
    accent: string;
    accentDark: string;
  }

  interface TypeSurface {
    accent: string;
    accentLight: string;
    background: string;
    recessed: string;
    elevated: string;
    elevatedHover: string;
    overlay: string;
    disabled: string;
    disabledSolid: string;
    brandLight: string;
    brandLightHover: string;
    brand: string;
    brandHover: string;
    brandDark: string;
    brandDarkHover: string;
    brandDarkOnAccentLight: string;
    brandDarkHoverOnAccentLight: string;
  }

  interface TypeBorder {
    subtle: string;
    default: string;
    emphasised: string;
    strong: string;
    active: string;
    focus: string;
    error: string;
    disabled: string;
  }

  interface TypeBackground {
    subtle: string;
  }
}
